import { Button, TextInput, Flex, Text, Badge } from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import ScrollableTable from "src/components/shared/ScrollableTable/ScrollableTable";
import { useCheckPerm, useGetApi } from "src/hooks";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";

import BackToYachtButton from "../BackToYachtButton/BackToYachtButton";
import SetMultipleRotation from "./SetMultipleRotation/SetMultipleRotation";
import { type IUser } from "./UserList.types";
import UserListRow from "./UserListRow";

const headers = [
  {
    name: "extref",
    label: "Ext. Ref No.",
  },
  {
    name: "fname",
    label: "First Name",
    style: { width: "20%" },
  },
  {
    name: "sname",
    label: "Surname",
    style: { width: "20%" },
  },
  {
    name: "ref",
    label: "Ref",
    style: { width: "5%" },
  },
  {
    name: "role",
    label: "Role",
    style: { width: "20%" },
  },
  {
    name: "email",
    label: "Email",
    style: { width: "25%" },
  },
  {
    name: "blank",
    label: "Active / Rotation",
    style: { width: "7%" },
  },
];

interface Props {
  onUserSelect: (user: IUser) => void;
  onAddUser?: () => void;
  showAddButton?: boolean;
  useManagementLinksIfAvailable?: boolean;
  showChangeMultiple?: boolean;
}

const CrewSetUp = ({
  onUserSelect,
  showAddButton,
  onAddUser,
  useManagementLinksIfAvailable,
  showChangeMultiple,
}: Props) => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [showRotationChange, setShowRotationChange] = useState(false);
  const [changedUsers, setChangedUsers] = useState<IUser[]>([]);
  const [search, setSearch] = useState("");
  const checkPerm = useCheckPerm();
  const [searchParams, setSearchParams] = useSearchParams();

  const mapFilteredUsers = useCallback((data: any) => {
    setUsers(data.list ? (data.list as IUser[]) : []);
    setActiveCount(data.active ?? 0);
    setInactiveCount(data.inactive ?? 0);
  }, []);

  const mapSortedUsers = useCallback((data: any) => {
    setUsers(data?.data?.content?.users ?? []);
  }, []);

  const { refetch: fetchFiltered } = useGetApi({
    request: "user-search-yacht",
    mapper: mapFilteredUsers,
    fetchOnInit: false,
  });

  const { refetch: fetchSorted } = useGetApi({
    request: "get-settings",
    body: { params: { sect: "_userlist" } },
    mapper: mapSortedUsers,
    fetchOnInit: false,
  });
  const refetchSorted = async () => {
    const status = searchParams.get("status");
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    if ((sort && dir) ?? status) {
      setInputValue("");
      setSearch("");
      const body: any = {
        _st: status ?? "1",
        params: {
          sort,
          dir,
          sect: "_userlist",
        },
      };

      await fetchSorted(body);
    }
  };
  const refetchFiltered = async () => {
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    if (!sort || !dir) {
      await fetchFiltered({
        _st: searchParams.get("status") ?? "1",
        params: { search, yachtid: getYachtId() },
      });
    }
  };
  const refetch = async () => {
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    if (!sort || !dir) await refetchFiltered();
    else await refetchSorted();
  };
  useEffect(() => {
    void refetchSorted();
  }, [fetchSorted, searchParams]);

  useEffect(() => {
    void refetchFiltered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFiltered, search, status]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(inputValue);
      setSearchParams(prev => {
        prev.delete("sort");
        prev.delete("dir");
        return prev;
      });
    }, 700);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const renderRow = useCallback(
    ({ row: user }: { row: IUser }) => (
      <UserListRow
        user={user}
        onUserSelect={onUserSelect}
        changedUsers={changedUsers}
        setChangedUsers={setChangedUsers}
        showRotationChange={showRotationChange}
      />
    ),
    [onUserSelect, showRotationChange, changedUsers, setChangedUsers],
  );

  const dynamicHeaders = useMemo(() => {
    if (showRotationChange) {
      return [
        ...headers,
        {
          name: "switch",
          label: "Set Active/Inactive",
          // style: { width: "7%" },
        },
      ];
    }
    return headers;
  }, [showRotationChange]);
  return (
    <PageContainer>
      <Breadcrumbs border={false}>
        <BackToYachtButton fetchLinks={useManagementLinksIfAvailable} />
        <ButtonsWrapper>
          {(activeCount > 0 || inactiveCount > 0) && (
            <Flex direction="row" gap={4}>
              <Badge
                variant="light"
                size="lg"
                color={"green.9"}
                py={10}
                miw={80}>
                <Flex gap={6} align="center">
                  Active
                  <Text fz="inherit" c="inherit">
                    {activeCount}
                  </Text>
                </Flex>
              </Badge>
              <Badge
                variant="light"
                size="lg"
                color={"orange.5"}
                py={10}
                miw={80}>
                <Flex gap={6} align="center">
                  Inactive
                  <Text fz="inherit" c="inherit">
                    {inactiveCount}
                  </Text>
                </Flex>
              </Badge>
            </Flex>
          )}
          <Select
            data={[
              { label: "All", value: "0" },
              { label: "Active", value: "1" },
              { label: "Inactive", value: "2" },
              { label: "Archived", value: "3" },
            ]}
            value={searchParams.get("status") ?? "1"}
            onChange={value => {
              setSearchParams(prev => {
                prev.set("status", value ?? "1");
                return prev;
              });
            }}
          />
          <TextInput
            placeholder="Search..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
          />
          {showAddButton && checkPerm("createuser") && (
            <Button variant="outline" onClick={onAddUser}>
              Add +
            </Button>
          )}
          {showChangeMultiple && (
            <SetMultipleRotation
              setChangedUsers={setChangedUsers}
              showRotationChange={showRotationChange}
              setShowRotationChange={setShowRotationChange}
              changedUsers={changedUsers}
              refetch={refetch}
            />
          )}
        </ButtonsWrapper>
      </Breadcrumbs>
      <ScrollableTable
        data={users}
        keyExtractor={item => item.userid}
        headers={dynamicHeaders}
        renderRow={renderRow}
        sortable
      />
    </PageContainer>
  );
};

export default CrewSetUp;
